import React from 'react';
import { FixedSchedule } from './FixedSchedule';
import {
  DAY_BTN_ACTIVE,
  DAY_BTN_INACTIVE,
  getDayPills,
  getWeekDateRange,
  WorkWeekValue,
} from './helpers';

// Constants
const ROTATION_OPTIONS = [2, 3, 4, 5, 6] as const;
const ROT_BTN_BASE =
  'tw-inline-flex tw-items-center tw-px-4 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-semibold tw-border tw-border-solid';

// Component
interface RotationalScheduleProps {
  onChange: (e: { value: WorkWeekValue }) => void;
  value?: WorkWeekValue;
  onBlur?: () => void;
  processedProps?: boolean;
}

export const RotationalSchedule = ({
  onChange,
  value,
  onBlur,
  processedProps,
}: RotationalScheduleProps) => {
  const initLength = (() => {
    const n = value?.weeks?.length ?? 0;
    return n >= 2 && n <= 6 ? n : null;
  })();

  const [rotationLength, setRotationLength] = React.useState<number | null>(
    initLength,
  );
  const [weeksData, setWeeksData] = React.useState<string[][]>(
    value?.weeks ?? [],
  );
  const [nonWorkingWeeks, setNonWorkingWeeks] = React.useState<boolean[]>(
    value?.nonWorkingWeeks ??
      (value?.weeks ?? []).map((w) => w.every((d) => d === '00:00')),
  );
  const weekRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  // Tracks the last value we emitted so incoming echoes of our own value
  // don't clobber in-progress input during re-hydration.
  const lastEmittedRef = React.useRef<string | undefined>(undefined);

  // Re-initialise state when the incoming form value changes, e.g. when
  // returning to a form that has hydrated previously saved answers.
  React.useEffect(() => {
    if (!processedProps) return;
    const incoming = JSON.stringify(value?.weeks ?? null);
    // Skip the echo of the value we just emitted ourselves.
    if (incoming === lastEmittedRef.current) return;
    const n = value?.weeks?.length ?? 0;
    const newLength = n >= 2 && n <= 6 ? n : null;
    setRotationLength(newLength);
    setWeeksData(value?.weeks ?? []);
    setNonWorkingWeeks(
      value?.nonWorkingWeeks ??
        (value?.weeks ?? []).map((w) => w.every((d) => d === '00:00')),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps, value]);

  React.useEffect(() => {
    if (rotationLength === null) return;
    const weeks = Array.from(
      { length: rotationLength },
      (_, i) => weeksData[i] ?? new Array<string>(7).fill('00:00'),
    );
    const nonWorking = Array.from(
      { length: rotationLength },
      (_, i) => nonWorkingWeeks[i] ?? false,
    );
    lastEmittedRef.current = JSON.stringify(weeks);
    onChange({ value: { weeks, nonWorkingWeeks: nonWorking } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeksData, rotationLength, nonWorkingWeeks]);

  const handleRotationSelect = (n: number) => {
    setRotationLength(n);
    setWeeksData((prev) =>
      Array.from(
        { length: n },
        (_, i) => prev[i] ?? new Array<string>(7).fill('00:00'),
      ),
    );
    setNonWorkingWeeks((prev) =>
      Array.from({ length: n }, (_, i) => prev[i] ?? false),
    );
  };

  const handleWeekChange =
    (weekIndex: number) => (e: { value: WorkWeekValue }) => {
      setWeeksData((prev) => {
        const next = [...prev];
        while (next.length <= weekIndex) {
          next.push(new Array<string>(7).fill('00:00'));
        }
        next[weekIndex] = e.value.weeks[0];
        return next;
      });
    };

  const handleNonWorking = (weekIndex: number, checked: boolean) => {
    setNonWorkingWeeks((prev) => {
      const next = [...prev];
      next[weekIndex] = checked;
      return next;
    });
    setWeeksData((prev) => {
      const next = [...prev];
      while (next.length <= weekIndex) {
        next.push(new Array<string>(7).fill('00:00'));
      }
      next[weekIndex] = new Array<string>(7).fill('00:00');
      return next;
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id="form-work-schedule-rotational"
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0 tw-bg-white tw-rounded-[8px] tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)]"
      onBlur={handleBlur}
    >
      {/* Rotation length selector*/}
      <div className="tw-px-5 tw-pt-6 tw-pb-5">
        <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-3">
          How many weeks are in your rotation?
        </p>
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
          {ROTATION_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              id={`form-work-schedule-rotation-${n}`}
              onClick={() => handleRotationSelect(n)}
              className={`${ROT_BTN_BASE} ${
                rotationLength === n ? DAY_BTN_ACTIVE : DAY_BTN_INACTIVE
              }`}
            >
              {n} weeks
            </button>
          ))}
        </div>
        <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-2">
          Starting with the current week, please list the days and hours for
          this week and each week that follows.
        </p>
        <p className="tw-text-sm tw-text-unm-adhoc2">
          (Include all weeks in the rotation, even off weeks. For example, if
          you work 3 weeks on and 1 week off, include all 4 weeks.)
        </p>
      </div>

      {/* Per-week schedule cards*/}
      {rotationLength !== null &&
        Array.from({ length: rotationLength }, (_, i) => (
          <div
            key={i}
            ref={(el) => {
              weekRefs.current[i] = el;
            }}
            id={`form-work-schedule-rotation-week-${i}`}
            className="tw-border-0 tw-border-t tw-border-solid tw-border-t-unm-mixed"
          >
            {/* Week header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-px-5 tw-pt-5 tw-pb-3">
              <div>
                <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2">
                  Week {i + 1}
                  {i === 0 ? ' (Current week)' : ''}
                </p>
                <p className="tw-text-sm tw-text-unm-adhoc2">
                  {getWeekDateRange(i)}
                </p>
              </div>
              <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-unm-adhoc2 tw-cursor-pointer">
                <input
                  type="checkbox"
                  id={`form-work-schedule-non-working-${i}`}
                  checked={nonWorkingWeeks[i] ?? false}
                  onChange={(e) => handleNonWorking(i, e.target.checked)}
                  className="tw-cursor-pointer"
                />
                <span>Mark as non-working week</span>
              </label>
            </div>
            {/* Standard week body – hidden when non-working */}
            {!nonWorkingWeeks[i] && (
              <FixedSchedule
                embedded
                processedProps={processedProps}
                onChange={handleWeekChange(i)}
                value={{
                  weeks: [weeksData[i] ?? new Array<string>(7).fill('00:00')],
                }}
              />
            )}
          </div>
        ))}

      {/* Summary*/}
      {rotationLength !== null && (
        <div className="tw-border-0 tw-border-t tw-border-solid tw-border-t-unm-mixed">
          <div
            id="form-work-schedule-rotation-summary"
            className="tw-border tw-rounded-[8px] tw-shadow-[0_1px_4px_rgba(0,0,0,0.12)] tw-mx-5 tw-my-5 tw-px-5 tw-py-5"
          >
            <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2 tw-mb-3">
              Summary
            </p>
            <div className="tw-flex tw-flex-col tw-gap-4">
              {Array.from({ length: rotationLength }, (_, i) => {
                const weekData =
                  weeksData[i] ?? new Array<string>(7).fill('00:00');
                const isNonWorking = nonWorkingWeeks[i] ?? false;
                const pills = isNonWorking ? null : getDayPills(weekData);
                let pillsContent: React.ReactNode;
                if (isNonWorking) {
                  pillsContent = (
                    <span
                      className={`${ROT_BTN_BASE} tw-bg-unm-bg-light tw-text-unm-adhoc2 tw-border-[#929292]`}
                    >
                      Non-working week
                    </span>
                  );
                } else if (pills && pills.length > 0) {
                  pillsContent = pills.map((label) => (
                    <span
                      key={label}
                      className={`${ROT_BTN_BASE} tw-bg-unm-bg-light tw-text-unm-adhoc2 tw-border-[#929292]`}
                    >
                      {label}
                    </span>
                  ));
                } else {
                  pillsContent = (
                    <span className="tw-opacity-60">No days selected</span>
                  );
                }
                return (
                  <div
                    key={i}
                    className="tw-flex tw-flex-col tw-gap-2 tw-text-sm tw-text-unm-adhoc2"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span className="tw-font-semibold">Week {i + 1}:</span>
                      <button
                        type="button"
                        onClick={() =>
                          weekRefs.current[i]?.scrollIntoView({
                            behavior: 'smooth',
                          })
                        }
                        className="tw-text-unm-interactive01-400 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-underline"
                      >
                        Go to week {i + 1}
                      </button>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {pillsContent}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </fieldset>
  );
};
